import config from "@/config";
import { boot } from "front-chat-sdk/helpers";
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@babackapp/auth-client/hooks";
import { useFrontChatHash } from "@babackapp/private-api-client/client/use-frontchat-hash";

// Ce champ doit exister dans Front (Settings > Contacts > Custom fields),
// sinon il est ignoré silencieusement.
const CURRENT_URL_FIELD = "Current URL";

// Chaque `boot` du SDK ajoute une balise script : `useFrontChatBoot` en
// rajoutait une à chaque montage, donc à chaque changement de boutique. Une
// seule par page ; un échec de chargement permet de réessayer.
let bootPromise: Promise<unknown> | null = null;
function bootFrontChatOnce(): Promise<unknown> {
  bootPromise ??= boot(document.body).catch((error: unknown) => {
    bootPromise = null;
    throw error;
  });
  return bootPromise;
}

function shutdownFrontChat() {
  try {
    window.FrontChat?.("shutdown");
  } catch {
    // Init pas encore terminée : `onInitCompleted` s'en charge.
  }
}

/**
 * Bulle de chat Front. Le layout ne la monte que si la boutique a le chat
 * (`isChatSupportEnabled`, BBS-406) : chat coupé = script Front jamais chargé.
 */
export function FrontChat() {
  const { user } = useAuth();
  const frontChatHashQuery = useFrontChatHash();
  const location = useLocation();

  const email = user?.emails[0]?.address;
  const userHash = frontChatHashQuery.data?.frontHash;

  const [isReady, setIsReady] = useState(false);
  const isMountedRef = useRef(true);
  const isInitRequestedRef = useRef(false);

  // Changer de boutique démonte ce composant (fallback du layout pendant le
  // chargement, ou boutique sans chat), mais la bulle injectée par Front
  // reste dans la page : on l'éteint. Si le chargement ou l'init sont encore
  // en cours, ils voient le composant démonté et n'affichent rien.
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (isInitRequestedRef.current) shutdownFrontChat();
    };
  }, []);

  // Dernière URL réellement transmise à Front, pour ne pas répéter l'appel.
  const lastSentUrlRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (
      !config.isProd ||
      isInitRequestedRef.current ||
      !user ||
      frontChatHashQuery.isLoading
    ) {
      return;
    }

    isInitRequestedRef.current = true;
    lastSentUrlRef.current = window.location.href;

    bootFrontChatOnce()
      .then(() => {
        if (!isMountedRef.current) return;
        window.FrontChat?.("init", {
          chatId: config.frontchatId,
          userHash,
          useDefaultLauncher: true,
          email,
          name: `${user.profile?.firstName || ""} ${user.profile?.lastName || ""}`.trim(),
          customFields: {
            [CURRENT_URL_FIELD]: lastSentUrlRef.current,
          },
          onInitCompleted: () => {
            // Démonté pendant l'init (boutique changée) : pas de bulle orpheline.
            if (!isMountedRef.current) {
              shutdownFrontChat();
              return;
            }
            setIsReady(true);
          },
        });
      })
      .catch((error: unknown) => {
        isInitRequestedRef.current = false;
        console.error("[FrontChat] boot failed", error);
      });
  }, [user, frontChatHashQuery.isLoading, config.frontchatId]);

  // Garde l'URL à jour à chaque navigation. `identity` remplace l'identité du
  // contact côté Front : sans email + userHash, Front ne sait pas à quel
  // contact rattacher les customFields et les ignore silencieusement. On
  // repasse donc les mêmes identifiants qu'à l'initialisation.
  useEffect(() => {
    if (!isReady || !email || !userHash) {
      return;
    }

    const currentUrl = window.location.href;
    if (lastSentUrlRef.current === currentUrl) {
      return;
    }
    lastSentUrlRef.current = currentUrl;

    window.FrontChat?.("identity", {
      email,
      userHash,
      customFields: {
        [CURRENT_URL_FIELD]: currentUrl,
      },
    });
  }, [
    isReady,
    email,
    userHash,
    location.pathname,
    location.search,
    location.hash,
  ]);

  return <></>;
}
